import { useId, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SectionLayout.module.css";

/**
 * A page's view controls, floating at the top right and staying put while the page scrolls: one small round button with a
 * sliders icon that opens, in place, into a pill of the controls. It opens when the pointer rests on it, or when it is
 * clicked, tapped or pressed, and closes when the pointer or focus leaves it, on Escape, or when a control asks it to (once
 * a layout is chosen, say). A control inside that handles Escape itself (a dropdown's own menu) closes only itself.
 *
 * @param props.wideOnly - Shows the button only on screens wide enough for the section grids, for a pill that holds nothing else.
 * @param props.children - The pill's controls, given a function that closes the pill and returns focus to its button.
 */
export function ViewOptions({ wideOnly = false, children }: { wideOnly?: boolean; children: (close: () => void) => ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Whether the pointer opened the pill by resting on it: the click that usually follows then keeps it open rather than closing it.
  const openedByHover = useRef(false);
  const menuId = useId();
  const close = (): void => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className={wideOnly ? styles.toolbarWideOnly : styles.toolbar}>
      <div
        className={styles.picker}
        data-open={open || undefined}
        // A mouse opens it by resting on it; a touch opens it with the tap, which a pointer-enter would otherwise undo.
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse" && !open) {
            openedByHover.current = true;
            setOpen(true);
          }
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse") {
            openedByHover.current = false;
            setOpen(false);
          }
        }}
        onBlur={(event) => !event.currentTarget.contains(event.relatedTarget) && setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && open && !event.defaultPrevented) {
            close();
          }
        }}
      >
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          aria-label={t("layout.viewOptions")}
          aria-expanded={open}
          aria-controls={menuId}
          onClick={() => {
            setOpen(openedByHover.current || !open);
            openedByHover.current = false;
          }}
        >
          <svg className={styles.icon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M2 4h7M12 4h2M2 8h2M7 8h7M2 12h9" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
            <circle cx="10.5" cy="4" r="1.5" fill="none" stroke="currentColor" strokeWidth={1.5} />
            <circle cx="5.5" cy="8" r="1.5" fill="none" stroke="currentColor" strokeWidth={1.5} />
            <circle cx="12.5" cy="12" r="1.5" fill="none" stroke="currentColor" strokeWidth={1.5} />
          </svg>
        </button>
        <div id={menuId} className={styles.menu}>
          {children(close)}
        </div>
      </div>
    </div>
  );
}
